import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { paymentApi } from "../../../utils/axios/Config";
import { toLocaleStringFormatter, unixToPersianDay, unixToPersianTime } from "../../../utils/utils";
import BackButton from "../../shared/BackButton";
import Loader from "../../shared/Loader";
import ReactShowMoreText from "../../shared/show-more";
import TransactionInfoCard from "../../shared/TransactionInfoCard";

const TransactionsInfo = () => {
  const { id } = useParams();
  const [transactionData, setTransactionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchTransaction = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await paymentApi.get(`/transactions/${id}/`, {
          responseType: "text",
        });
        const parsedData = parseTrackingCodes(response.data);
        setTransactionData(parsedData);
      } catch (err) {
        console.error("Failed to fetch transaction data", err);
        setError("مشکلی در دریافت اطلاعات تراکنش پیش آمد.");
      } finally {
        setLoading(false);
      }
    };

    fetchTransaction();
  }, [id]);

  const parseTrackingCodes = (data) => {
    if (!data) return {};
    const fixedData = data.replace(/("vanda_tracking_code":)(\d{18})/g, '$1"$2"');
    return JSON.parse(fixedData);
  };

  if (loading) {
    return (
      <div className="w-full h-full flex-center min-h-[400px]">
        <Loader />
      </div>
    );
  }

  if (error) {
    return <div className="w-full h-full flex-center text-red-500 min-h-[400px]">{error}</div>;
  }

  if (!transactionData) return null;

  const { transaction = {}, additional_data = {}, business = {}, type, title } = transactionData;

  const {
    amount,
    invoice_time,
    order_number,
    vanda_tracking_code,
    transaction_state,
    settlement_status,
    verified_by_user,
    callback,
    track_id,
    reference_number,
    payer_name,
    payer_postal_code,
    payer_national_code,
    payer_address,
    payer_mobile,
    payer_description,
  } = transaction;

  const { card_number = "", failure_reason = "", payment_time = "" } = additional_data;

  return (
    <div className="w-full bg-neutral-00 p-4 rounded-v-lg">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-title-lg-semibold border-b-2 border-neutral-30 pb-2">اطلاعات تراکنش</h1>
        <BackButton />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <InfoBanner label="تراکنش از" value={mapTransactionType(type)} extra={title} />

        <TransactionInfoCard label="مبلغ تراکنش (ریال)" value={toLocaleStringFormatter(amount)} />
        <TransactionInfoCard
          label="تاریخ تراکنش"
          value={`${unixToPersianTime(invoice_time) ?? "-"} - ${unixToPersianDay(invoice_time) ?? "-"}`}
        />
        <TransactionInfoCard label="شماره سفارش" value={order_number} />
        <TransactionInfoCard label="کد رهگیری وندا" value={vanda_tracking_code} />

        {transactionData?.gateway && <TransactionInfoCard label="آدرس بازگشتی" value={callback} />}

        <TransactionInfoCard label="وضعیت تراکنش" value={mapTransactionStatus(transaction_state)} />

        {transaction_state === "success" && (
          <TransactionInfoCard label="وضعیت تسویه" value={mapSettlementStatus(settlement_status)} />
        )}

        <TransactionInfoCard label="وضعیت تایید پذیرنده" value={verified_by_user ? "تایید" : "عدم تایید"} />

        {transaction_state === "success" && (
          <>
            <TransactionInfoCard label="کد رهگیری" value={track_id} />
            <TransactionInfoCard
              label="شماره کارت پرداخت کننده"
              value={<span dir="ltr">{card_number || "وارد نشده"}</span>}
            />
            <TransactionInfoCard label="شماره ارجاع" value={reference_number} />
            <TransactionInfoCard
              label="تاریخ پرداخت"
              value={`${unixToPersianTime(payment_time) ?? "-"} - ${unixToPersianDay(payment_time) ?? "-"}`}
            />
          </>
        )}

        {/* <InfoBanner label="بیزینس" value={business.fa_name} /> */}

        {transaction_state !== "success" && (
          <TransactionInfoCard label="دلیل ناموفق بودن تراکنش" value={failure_reason || "وارد نشده"} />
        )}

        <InfoBanner label="مشخصات پرداخت کننده" />

        <TransactionInfoCard label="نام پرداخت کننده" value={payer_name || "وارد نشده"} />
        <TransactionInfoCard label="کد پستی پرداخت کننده" value={payer_postal_code || "وارد نشده"} />
        <TransactionInfoCard label="کد ملی پرداخت کننده" value={payer_national_code || "وارد نشده"} />
        <TransactionInfoCard label="آدرس پرداخت کننده" value={payer_address || "وارد نشده"} />
        <TransactionInfoCard label="موبایل پرداخت کننده" value={payer_mobile || "وارد نشده"} />
      </div>

      <TransactionTextArea label="شرح پرداخت" value={payer_description || "وارد نشده"} />
    </div>
  );
};

const InfoBanner = ({ label, value, extra }) => (
  <div className="col-span-2 flex gap-2 bg-primary-30 rounded-v-lg p-2">
    <p>
      {label} {extra && <span>{extra}</span>}:
    </p>
    {value && <p>{value}</p>}
  </div>
);

const mapTransactionType = (type) => {
  switch (type) {
    case "gateway":
      return "درگاه";
    case "form":
      return "فرم";
    default:
      return "کارتخوان";
  }
};

const mapTransactionStatus = (status) => {
  const statusMap = {
    failed: "ناموفق",
    success: "موفق",
    in_progress: "در حال انجام",
    success_pending: "موفق - در انتظار",
  };
  return statusMap[status] || "";
};

const mapSettlementStatus = (status) => {
  const statusMap = {
    paid: "تسویه شده",
    pending_for_settlement: "در انتظار تسویه",
    submitted: "آماده تسویه",
  };
  return statusMap[status] || "";
};

const TransactionTextArea = ({ label, value }) => (
  <div className="flex flex-col gap-2 rounded-v-lg p-2 bg-secondary-10 mt-4">
    <p className="text-title-md-semibold-UL">{label}</p>
    {value && (
      <ReactShowMoreText
        lines={3}
        more="نمایش بیشتر"
        less="نمایش کمتر"
        className="bg-neutral-10 break-words p-2"
        anchorClass="text-dark cursor-pointer text-[10px]"
        expanded={false}
        width={0}
        truncatedEndingComponent={"... "}
      >
        {value}
      </ReactShowMoreText>
    )}
  </div>
);

export default TransactionsInfo;
